import { makeAutoObservable } from 'mobx'

export type Module2TabType = 'home' | 'chat' | 'roleplay' | 'review' | string // string for dynamic tabs like 'chat-1', 'review-2'
export type ChatMode = 'selection' | 'chat' | 'voice'

export interface ModuleData {
  id: number
  name: string
  userCount: number
  maxUsers: number
  coachFlowId: number
  roleplayFlowId: number
  reviewPromptPayloadId: number
  timerDuration: number
  defaultRemainingTime: number
  reviewPageSize: number
  /** B2C only: roleplays on this module spend the user's minute balance. */
  metered?: boolean | null
  /** Seconds left on this module. Null when not metered. */
  balanceSeconds?: number | null
}

class Module2Store {
  activeTab: Module2TabType = 'home'
  reviewEnabled: boolean = false
  selectedRolePlay: number | null = null // No default selection
  mode: ChatMode = 'selection'
  rolePlayContext: {
    rolePlay?: number
    conversation?: Array<{ role: string; content: string }>
  } = {}
  isTimerActive: boolean = false
  remainingTime: number = 300 // 5 minutes in seconds

  // Per-module memory of last selected role play card
  selectedByModule: Record<number, number> = {}

  // Dynamic modules
  modules: ModuleData[] = []
  activeModuleId: number | null = null
  openModuleIds: number[] = [] // Track which module dropdowns are open

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  /** The active module is metered and has too little left to start a roleplay. */
  get activeModuleIsOutOfMinutes(): boolean {
    const active = this.activeModuleConfig
    return !!active?.metered && (active.balanceSeconds ?? 0) < 60
  }

  get activeModuleConfig(): ModuleData | undefined {
    if (this.activeModuleId === null) return undefined
    return this.modules.find((m) => m.id === this.activeModuleId)
  }

  setActiveTab(tab: Module2TabType) {
    this.activeTab = tab
  }

  setReviewEnabled(enabled: boolean) {
    this.reviewEnabled = enabled
  }

  setSelectedRolePlay(rolePlay: number | null) {
    this.selectedRolePlay = rolePlay
    if (this.activeModuleId != null && rolePlay != null) {
      this.selectedByModule[this.activeModuleId] = rolePlay
    }
  }

  restoreSelectionForModule(moduleId: number) {
    this.selectedRolePlay = this.selectedByModule[moduleId] ?? null
  }

  setMode(mode: ChatMode) {
    this.mode = mode
  }

  setModules(modules: ModuleData[]) {
    this.modules = modules
    // Keep whichever dropdowns are already open (dropping ids of modules that no longer exist).
    // Module2Chat re-syncs the module list every time its tab mounts, so resetting here
    // collapsed the dropdown the user had just clicked a tab in. The initial open (desktop)
    // / closed (mobile) state is driven by Module2Page from the viewport.
    const ids = new Set(modules.map((m) => m.id))
    this.openModuleIds = this.openModuleIds.filter((id) => ids.has(id))
  }

  /** Balances change after every metered roleplay; the rest of a module does not. */
  setBalances(balances: { id: number; metered?: boolean | null; balanceSeconds?: number | null }[]) {
    const byId = new Map(balances.map((b) => [b.id, b]))
    this.modules = this.modules.map((m) => {
      const fresh = byId.get(m.id)
      return fresh ? { ...m, metered: fresh.metered, balanceSeconds: fresh.balanceSeconds } : m
    })
  }

  openAllModules() {
    this.openModuleIds = this.modules.map((m) => m.id)
  }

  setActiveModuleId(id: number | null) {
    this.activeModuleId = id
  }

  toggleModuleOpen(moduleId: number) {
    if (this.openModuleIds.includes(moduleId)) {
      this.openModuleIds = this.openModuleIds.filter((id) => id !== moduleId)
    } else {
      this.openModuleIds = [...this.openModuleIds, moduleId]
    }
  }

  isModuleOpen(moduleId: number): boolean {
    return this.openModuleIds.includes(moduleId)
  }

  closeAllModules() {
    this.openModuleIds = []
  }

  setRolePlayContext(data: { rolePlay?: number; conversation?: Array<{ role: string; content: string }> }) {
    this.rolePlayContext = data
  }

  setIsTimerActive(active: boolean) {
    this.isTimerActive = active
  }

  setRemainingTime(time: number) {
    this.remainingTime = time
  }

  resetTimer() {
    this.remainingTime = this.activeModuleConfig?.defaultRemainingTime ?? 300
    this.isTimerActive = false
  }

  finishRolePlayAndStartReview(data: { rolePlay?: number; conversation?: Array<{ role: string; content: string }> }) {
    this.setRolePlayContext(data)
    this.setReviewEnabled(true)
    this.setMode('selection')
    this.setActiveTab(this.activeModuleId !== null ? `review-${this.activeModuleId}` : 'review')
    this.resetTimer()
  }

  clearReviewState() {
    this.setReviewEnabled(false)
    this.setRolePlayContext({})
    this.resetTimer()
  }
}

export const module2Store = new Module2Store()
